/**
 * How finished-job messages from the server become one toast
 * (components/JobDoneNotifier.js).
 */

const ACTION_LABELS = {
  captions: 'Open My Clips',
  'ai-clips': 'See clip ideas',
  transcription: 'Open AI Clips',
  'deck-render': 'Open the deck',
  'post-proposals': 'Review posts',
  week: 'Review your week',
};

const PLURAL = {
  captions: { ready: (n) => `Captions are ready on ${n} clips`, failed: (n) => `Captions couldn't be added to ${n} clips` },
  'ai-clips': { ready: (n) => `Clip ideas are ready for ${n} recordings`, failed: (n) => `Clip ideas couldn't be made for ${n} recordings` },
  transcription: { ready: (n) => `${n} videos are transcribed`, failed: (n) => `${n} videos couldn't be transcribed` },
  'deck-render': { ready: (n) => `${n} deck recordings are ready`, failed: (n) => `${n} deck recordings didn't finish` },
  'post-proposals': { ready: () => 'Posts are waiting for your approval', failed: () => 'Posts are waiting for your approval' },
  week: { ready: (n) => `${n} weeks are ready to review`, failed: (n) => `${n} weeks couldn't be prepared` },
};

/** Only paths inside the app; the server builds these, but never navigate off-site. */
function safePath(p) {
  return typeof p === 'string' && /^\/[^/\\]/.test(p) ? p : null;
}

/**
 * One toast for a batch of payloads arriving together. Failures first:
 * a host who had one fail and nine succeed must not miss the one.
 */
export function jobDoneToast(batch) {
  const items = (batch || []).filter((p) => p && PLURAL[p.kind] && (p.status === 'ready' || p.status === 'failed'));
  if (!items.length) return null;
  const failed = items.filter((p) => p.status === 'failed');
  const group = failed.length ? failed : items;
  const first = group[0];
  const sameKind = group.every((p) => p.kind === first.kind);
  const type = first.status === 'failed' ? 'error' : 'success';
  if (group.length === 1) {
    return {
      type,
      title: first.title,
      message: first.itemTitle ? `${first.itemTitle}. ${first.message}` : first.message,
      path: safePath(first.path),
      actionLabel: ACTION_LABELS[first.kind],
    };
  }
  return {
    type,
    title: sameKind ? PLURAL[first.kind][first.status](group.length) : (type === 'error' ? `${group.length} jobs didn't finish` : `${group.length} jobs are done`),
    message: first.message,
    // A batch opens the page, not one video picked out of it.
    // (Proposals have one page for all of them, so theirs keeps its query.)
    path: sameKind ? safePath(first.kind === 'post-proposals' ? first.path : String(first.path || '').split('?')[0]) : null,
    actionLabel: ACTION_LABELS[first.kind],
  };
}

/** No finished-job toasts in the broadcast studio (or its setup page). */
export function isQuietPath(pathname) {
  return /^\/(studio|setup)(\/|$)/.test(pathname || '');
}
